import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { ConfigService } from './config.service';

/**
 * Comando de voz durante el PIENSE. `answer_now` abre la ventana de respuesta;
 * pass/repeat/clarify cierran el piense y actúan sobre el turno. `none` = seguir
 * pensando. Sin `end` (retirado del flujo, PR 7236/7239).
 */
export type ThinkingCommand = 'answer_now' | 'pass' | 'repeat' | 'clarify' | 'context' | 'pause' | 'none';

const INSTRUCTIONS =
  'El candidato está en el tiempo de PENSAR antes de responder una pregunta de entrevista. ' +
  'Interpreta la INTENCIÓN de lo que dijo y clasifícala en un comando. No te limites a las ' +
  'palabras exactas ni a los ejemplos: reconoce sinónimos, jerga y frases equivalentes. Si ya ' +
  'está razonando o dando contenido, es none. Ante duda, none.';

/** criteria map: describe la INTENCIÓN; los ejemplos son muestra, no lista cerrada. */
const CRITERIA: Record<ThinkingCommand, string> = {
  answer_now:
    'quiere EMPEZAR A RESPONDER ya, terminar el tiempo de pensar. Ej: "quiero responder", "quiero contestar", "ya estoy listo", "empecemos"',
  pass: 'quiere avanzar o saltar a la siguiente pregunta. Ej: "pasa", "siguiente", "pasemos", "saltémosla"',
  clarify:
    'NO entendió el sentido de la pregunta o pide que la digan de otra forma (reformular). Ej: "no entendí", "no comprendo", "reformúlame", "explícamela de otra manera"',
  context:
    'pide el CONTEXTO, el escenario o el ámbito de la pregunta para ubicarse (sí entendió las palabras). Ej: "dame contexto", "dame un poco más de contexto", "¿en qué contexto?", "ponme en situación"',
  repeat:
    'quiere volver a OÍR la MISMA pregunta idéntica por audio (no reformular). Ej: "repite", "repítela", "no te escuché"',
  pause:
    'pide una PAUSA o un momento para atender un imprevisto (no tiempo para pensar la respuesta). Ej: "quiero una pausa", "necesito una pausa", "dame un momento, tengo un imprevisto"',
  none: 'está pensando en voz alta, razonando, o nada de lo anterior',
};

interface DecisionResponse {
  data?: { choice?: string };
}

function isThinkingCommand(v: unknown): v is ThinkingCommand {
  return (
    v === 'answer_now' ||
    v === 'pass' ||
    v === 'repeat' ||
    v === 'clarify' ||
    v === 'context' ||
    v === 'pause' ||
    v === 'none'
  );
}

/**
 * Clasificador de comandos de voz del PIENSE vía decision-api
 * (POST /api/v2/decisions, Nova Micro tool-use). Reemplaza el regex del front.
 * Solo se usa en el piense (LOW no ve el piense); la respuesta la clasifica LOW.
 * Auth: AuthInterceptor agrega Bearer (Cognito) + acá se suma x-api-key. En local
 * el LocalOverrideInterceptor reescribe la url a :3005. NUNCA lanza: fallo → null
 * → el caller trata como "sin comando" y el piense sigue por tiempo.
 * Seguridad: tool_use forzado acota el output al enum + guardrail prompt-attack;
 * una injection a lo sumo sesga el choice a un comando válido.
 */
@Injectable({ providedIn: 'root' })
export class CommandClassifierService {
  constructor(
    private http: HttpClient,
    private config: ConfigService,
  ) {}

  /**
   * Pre-warm del lambda decision (cold start): dispara un classify dummy al
   * arrancar la sesión, antes del primer comando real, para que la primera
   * clasificación de verdad pegue en un lambda ya caliente. Fire-and-forget:
   * no espera ni propaga (classifyThinkingCommand ya traga errores).
   */
  warmUp(): void {
    void this.classifyThinkingCommand('hola').catch(() => undefined);
  }

  async classifyThinkingCommand(utterance: string): Promise<ThinkingCommand | null> {
    const text = (utterance || '').trim();
    if (!text) return null;
    try {
      const headers = new HttpHeaders().set('x-api-key', this.config.apiKey);
      const res = await firstValueFrom(
        this.http
          .post<DecisionResponse>(
            this.config.urlDecisions,
            { input: text, instructions: INSTRUCTIONS, criteria: CRITERIA },
            { headers },
          )
          .pipe(timeout(4000)),
      );
      const choice = res?.data?.choice;
      return isThinkingCommand(choice) ? choice : null;
    } catch {
      return null;
    }
  }
}
